import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

import { companyApi } from '@/api';
import { Button, Icon, useToast } from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';

import { letterFileError } from './offerLetterFile';

// const MAX_LETTER_BYTES = 5 * 1024 * 1024;

/**
 * The letter line under an offer: which letter the candidate gets, a preview,
 * and -- while the offer is still open -- upload or remove a signed PDF.
 * Without an upload the candidate gets a letter generated from the terms.
 */
export function OfferLetterControls({ offer, canChange = true, prominent = false }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const inputRef = useRef(null);
  const name = offer.candidate.full_name;

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['company-offers'] });
  }

  const preview = useMutation({
    mutationFn: () => companyApi.downloadOfferLetter(offer.id),
    onError: (error) => toast.error(errorMessage(error, 'Could not open the letter')),
  });

  const upload = useMutation({
    mutationFn: (file) => companyApi.uploadOfferLetter(offer.id, file),
    onSuccess: () => {
      toast.success(`Signed letter attached to the offer to ${name}`);
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not upload the letter')),
  });

  const remove = useMutation({
    mutationFn: () => companyApi.removeOfferLetter(offer.id),
    onSuccess: () => {
      toast.success('Signed letter removed. The generated letter is used again.');
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not remove the letter')),
  });

  function onPick(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    // const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    // if (!isPdf) {
    //   toast.error('Upload the offer letter as a PDF.');
    //   return;
    // }
    // if (file.size > MAX_LETTER_BYTES) {
    //   toast.error('The letter is too large. The limit is 5 MB.');
    //   return;
    // }
    const problem = letterFileError(file);
    if (problem) {
      toast.error(problem);
      return;
    }
    upload.mutate(file);
  }

  const canSwap = canChange && offer.is_open;
  const picker = (
    <input
      ref={inputRef}
      type="file"
      accept="application/pdf,.pdf"
      className="hidden"
      aria-label={`Signed offer letter for ${name}`}
      onChange={onPick}
    />
  );

  // A labelled block with real buttons, for a page where the letter is one
  // of the main things to find rather than a line under a list row.
  if (prominent) {
    return (
      <div className="border-line bg-surface-muted mt-3 rounded-lg border p-3">
        <div className="flex items-center gap-2.5">
          <span
            aria-hidden="true"
            className="bg-brand-50 text-brand-600 grid size-8 shrink-0 place-items-center rounded-md"
          >
            <Icon name="document" size={16} />
          </span>
          <div className="min-w-0">
            <p className="text-ink text-sm font-medium">Offer letter</p>
            <p
              className="text-ink-muted truncate text-xs"
              title={offer.has_uploaded_letter ? offer.letter_file_name : undefined}
            >
              {offer.has_uploaded_letter
                ? `Signed PDF · ${offer.letter_file_name}`
                : 'Generated from the offer terms'}
            </p>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            variant="secondary"
            size="sm"
            title={`Open the letter for ${name}`}
            isLoading={preview.isPending}
            onClick={() => preview.mutate()}
          >
            <Icon name="eye" size={14} />
            View letter
          </Button>
          {canSwap &&
            (offer.has_uploaded_letter ? (
              <Button
                variant="ghostDanger"
                size="sm"
                title={`Remove the signed letter for ${name}`}
                isLoading={remove.isPending}
                onClick={() => remove.mutate()}
              >
                <Icon name="trash" size={14} />
                Remove signed PDF
              </Button>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  title={`Upload a signed letter for ${name}`}
                  isLoading={upload.isPending}
                  onClick={() => inputRef.current?.click()}
                >
                  <Icon name="upload" size={14} />
                  Upload signed PDF
                </Button>
                {picker}
              </>
            ))}
        </div>
      </div>
    );
  }

  return (
    <div className="text-ink-muted mt-1.5 flex flex-wrap items-center gap-x-1 gap-y-0.5 text-xs">
      <Icon name="document" size={13} className="shrink-0" />
      <span
        className="max-w-44 truncate"
        title={offer.has_uploaded_letter ? offer.letter_file_name : undefined}
      >
        {offer.has_uploaded_letter ? offer.letter_file_name : 'Generated letter'}
      </span>
      <Button
        variant="ghost"
        size="xs"
        title={`Preview the letter for ${name}`}
        isLoading={preview.isPending}
        onClick={() => preview.mutate()}
      >
        <Icon name="eye" size={14} />
        Preview
      </Button>
      {canSwap &&
        (offer.has_uploaded_letter ? (
          <Button
            variant="ghostDanger"
            size="xs"
            title={`Remove the signed letter for ${name}`}
            isLoading={remove.isPending}
            onClick={() => remove.mutate()}
          >
            <Icon name="trash" size={14} />
            Remove
          </Button>
        ) : (
          <>
            <Button
              variant="ghost"
              size="xs"
              title={`Upload a signed letter for ${name}`}
              isLoading={upload.isPending}
              onClick={() => inputRef.current?.click()}
            >
              <Icon name="upload" size={14} />
              Upload signed PDF
            </Button>
            {picker}
          </>
        ))}
    </div>
  );
}
